import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowLeft, CheckCircle2, Mail, RefreshCw, Search, Clock, MessageCircle } from "lucide-react";
import { Link, useParams } from "react-router-dom";
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

const RESEND_COOLDOWN_SECONDS = 60;
const SUPPORT_EMAIL = "support@piktable.ch";

const ForgotPassword = () => {
  const { toast } = useToast();
  const { slug } = useParams();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  const loginPath = slug ? `/${slug}/login` : "/login";

  // Décompte du cooldown pour le bouton "Renvoyer"
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setTimeout(() => setResendCooldown((s) => s - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  const isValidEmail = (value: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

  const sendResetEmail = async (showToastOnSuccess = false) => {
    setLoading(true);

    try {
      // Appel de notre Edge Function brandée + traçable
      // (remplace supabase.auth.resetPasswordForEmail pour envoyer
      // un email Piktable via Resend et logguer chaque demande)
      const { error } = await supabase.functions.invoke("request-password-reset", {
        body: { email, slug: slug ?? null },
      });

      if (error) {
        const status = (error as any).status ?? (error as any).context?.status;
        if (status === 429) {
          toast({
            title: "Trop de tentatives",
            description:
              "Merci de patienter une minute avant de redemander un nouveau lien.",
            variant: "destructive",
          });
          setResendCooldown(RESEND_COOLDOWN_SECONDS);
          return false;
        }
        // Anti-énumération : on log mais on n'expose pas l'erreur
        console.error("[ForgotPassword] request-password-reset error:", error);
      }

      if (showToastOnSuccess) {
        toast({
          title: "Email renvoyé",
          description: "Si l'adresse existe, un nouveau lien vient d'être envoyé.",
        });
      }
      setResendCooldown(RESEND_COOLDOWN_SECONDS);
      return true;
    } catch (error) {
      console.error("[ForgotPassword] Unexpected error:", error);
      setResendCooldown(RESEND_COOLDOWN_SECONDS);
      return true;
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!isValidEmail(email)) {
      toast({
        title: "Adresse invalide",
        description: "Merci de vérifier le format de votre adresse email.",
        variant: "destructive",
      });
      return;
    }

    await sendResetEmail(false);
    setSubmitted(true);
  };

  const handleResend = async () => {
    if (resendCooldown > 0 || loading) return;
    await sendResetEmail(true);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary/5 via-background to-accent/5">
      <div className="w-full max-w-md px-4">
        <Button asChild variant="ghost" className="mb-4">
          <Link to={loginPath}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Retour à la connexion
          </Link>
        </Button>

        <Card className="p-8">
          {submitted ? (
            <div className="space-y-5">
              <div className="text-center space-y-3">
                <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                  <CheckCircle2 className="w-8 h-8 text-green-600" />
                </div>
                <h1 className="text-2xl font-serif font-bold">Vérifiez votre boîte mail</h1>
                <p className="text-muted-foreground text-sm">
                  Si un compte existe avec l'adresse <strong>{email}</strong>, un lien de
                  réinitialisation vient d'être envoyé.
                </p>
              </div>

              {/* Checklist d'aide */}
              <div className="bg-muted/40 rounded-lg p-4 space-y-2.5 text-sm">
                <p className="font-medium text-foreground">Vous ne trouvez pas l'email ?</p>
                <ul className="space-y-2 text-muted-foreground">
                  <li className="flex gap-2">
                    <Search className="w-4 h-4 mt-0.5 shrink-0" />
                    <span>Vérifiez votre dossier <strong>spam / courriers indésirables</strong>.</span>
                  </li>
                  <li className="flex gap-2">
                    <Clock className="w-4 h-4 mt-0.5 shrink-0" />
                    <span>L'email peut mettre <strong>2 à 3 minutes</strong> à arriver.</span>
                  </li>
                  <li className="flex gap-2">
                    <Mail className="w-4 h-4 mt-0.5 shrink-0" />
                    <span>Vérifiez l'<strong>orthographe</strong> de votre adresse.</span>
                  </li>
                </ul>
                <p className="text-xs text-muted-foreground pt-1">
                  Le lien est valide pendant <strong>1 heure</strong>.
                </p>
              </div>

              {/* Bouton renvoyer */}
              <Button
                onClick={handleResend}
                disabled={loading || resendCooldown > 0}
                variant="outline"
                className="w-full"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
                {resendCooldown > 0
                  ? `Renvoyer dans ${resendCooldown}s`
                  : loading
                  ? "Envoi en cours..."
                  : "Renvoyer l'email"}
              </Button>

              {/* Contact support */}
              <div className="border-t pt-4 text-center space-y-2">
                <p className="text-xs text-muted-foreground">
                  Toujours rien après 5 minutes ?
                </p>
                <a
                  href={`mailto:${SUPPORT_EMAIL}?subject=Probl%C3%A8me%20r%C3%A9initialisation%20mot%20de%20passe&body=Bonjour%2C%0A%0AJe%20n%27ai%20pas%20re%C3%A7u%20l%27email%20de%20r%C3%A9initialisation%20pour%20l%27adresse%20%3A%20${encodeURIComponent(email)}%0A%0AMerci.`}
                  className="inline-flex items-center gap-1.5 text-sm text-primary hover:underline"
                >
                  <MessageCircle className="w-4 h-4" />
                  Contacter le support
                </a>
              </div>

              <Button asChild variant="ghost" className="w-full">
                <Link to={loginPath}>Retour à la connexion</Link>
              </Button>
            </div>
          ) : (
            <>
              <div className="text-center mb-8">
                <div className="mx-auto w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mb-4">
                  <Mail className="w-6 h-6 text-primary" />
                </div>
                <h1 className="text-3xl font-serif font-bold mb-2">
                  Mot de passe oublié ?
                </h1>
                <p className="text-muted-foreground text-sm">
                  Entrez votre email, nous vous enverrons un lien pour réinitialiser
                  votre mot de passe.
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="vous@exemple.com"
                    required
                    disabled={loading}
                    autoComplete="email"
                  />
                </div>

                <Button
                  type="submit"
                  className="w-full"
                  disabled={loading || !email || !isValidEmail(email)}
                >
                  {loading ? "Envoi en cours..." : "Envoyer le lien de réinitialisation"}
                </Button>
              </form>
            </>
          )}
        </Card>
      </div>
    </div>
  );
};

export default ForgotPassword;
